import { useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useOrg } from "../lib/session";
import { providerName, sendable, useEmail, useSavedEmails } from "../lib/email";
import { DIALECTS } from "./AgentBuilder";
import { ErrorText, Field } from "./ui";

type Assist = "draft" | "reply" | "improve" | "shorter" | "formal" | "friendly" | "arabic" | "english";

// EmailComposer writes a new email to a contact, or a reply in an email
// conversation, with saved emails and AI help (drafts only: nothing is sent
// until the person presses Send).
/** Audience of a group email: chosen contacts, or everyone (optionally one stage). */
export interface EmailAudience { contact_ids?: string[]; all?: boolean; stage?: string; marketing?: boolean }

export function EmailComposer({ contactId, toAddress, group, threadId, defaultSubject, onSent, onGroupSent, onCancel }: {
  contactId?: string; toAddress?: string; group?: EmailAudience; threadId?: string; defaultSubject?: string;
  onSent?: (threadId: string) => void; onGroupSent?: (sent: number) => void; onCancel?: () => void;
}) {
  const { base, orgId, memberId } = useOrg();
  const qc = useQueryClient();
  const ov = useEmail();
  const saved = useSavedEmails();
  const boxes = sendable(ov.data, memberId);
  const [account, setAccount] = useState("");
  const [subject, setSubject] = useState(defaultSubject ?? "");
  const [body, setBody] = useState("");
  const [instruction, setInstruction] = useState("");
  const [dialect, setDialect] = useState("gulf");
  const [undo, setUndo] = useState<{ subject: string; body: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [sent, setSent] = useState(false);
  const from = account || boxes[0]?.id || "";
  const isReply = !!threadId;

  if (ov.data && !ov.data.enabled_in_plan) return <p className="muted small">Email isn't part of your plan.</p>;
  if (ov.data && !isReply && boxes.length === 0) {
    return <p className="muted small">Connect a mailbox under <Link to={`/o/${orgId}/settings/email`}>Email</Link> to send from SVENEX.</p>;
  }

  async function assist(action: Assist) {
    setBusy(action); setError(null);
    try {
      const out = await api<{ subject: string; body: string }>(`${base}/email/assist`, { method: "POST", json: {
        action, text: body, subject, instruction, dialect, ...(threadId ? { thread_id: threadId } : contactId ? { contact_id: contactId } : {}),
      } });
      setUndo({ subject, body });
      setBody(out.body);
      if (out.subject && !isReply) setSubject(out.subject);
      setInstruction("");
    } catch (err) { setError(err); } finally { setBusy(null); }
  }

  async function send() {
    setBusy("send"); setError(null);
    try {
      if (group) {
        const out = await api<{ sent: number }>(`${base}/email/groups`, { method: "POST", json: { ...group, account_id: from, subject, body } });
        setBody(""); setSubject(""); setUndo(null);
        qc.invalidateQueries({ queryKey: [orgId] });
        onGroupSent?.(out.sent);
        return;
      }
      const out = await api<{ thread_id: string }>(`${base}/email/messages`, { method: "POST", json: isReply
        ? { thread_id: threadId, body, ...(subject ? { subject } : {}) }
        : { account_id: from, ...(contactId ? { contact_id: contactId } : { to: toAddress }), subject, body } });
      setBody(""); setUndo(null); setSent(true);
      if (!isReply) setSubject("");
      qc.invalidateQueries({ queryKey: [orgId] });
      onSent?.(out.thread_id);
    } catch (err) { setError(err); } finally { setBusy(null); }
  }

  return (
    <div className="email-composer">
      {!isReply && (
        <div className="row">
          <Field label="From">
            <select value={from} onChange={(e) => setAccount(e.target.value)}>
              {boxes.map((b) => <option key={b.id} value={b.id}>{b.email_address} ({providerName[b.provider]}{b.kind === "personal" ? ", yours" : ""})</option>)}
            </select>
          </Field>
          {(saved.data?.templates.length ?? 0) > 0 && (
            <Field label="Start from a saved email">
              <select value="" onChange={(e) => {
                const tp = saved.data!.templates.find((x) => x.id === e.target.value.slice(3));
                if (!tp) return;
                const ar = e.target.value.startsWith("ar:");
                setUndo({ subject, body });
                setSubject(ar ? tp.subject_ar || tp.subject : tp.subject || tp.subject_ar);
                setBody(ar ? tp.body_ar || tp.body : tp.body || tp.body_ar);
              }}>
                <option value="">Choose…</option>
                {saved.data!.templates.flatMap((tp) => [
                  tp.body ? <option key={tp.id + "en"} value={"en:" + tp.id}>{tp.name}</option> : null,
                  tp.body_ar ? <option key={tp.id + "ar"} value={"ar:" + tp.id}>{tp.name} (عربي)</option> : null,
                ])}
              </select>
            </Field>
          )}
        </div>
      )}
      {!isReply && <Field label="Subject"><input value={subject} maxLength={200} onChange={(e) => setSubject(e.target.value)} dir="auto" /></Field>}
      <Field label={isReply ? "Reply" : "Message"}>
        <textarea rows={isReply ? 4 : 8} maxLength={20000} value={body} onChange={(e) => setBody(e.target.value)} dir="auto"
          placeholder="Write here, or ask the AI below. {{customer_first_name}}, {{business}} and {{my_name}} are filled in for you." />
      </Field>
      <div className="ai-bar">
        <input value={instruction} maxLength={1000} onChange={(e) => setInstruction(e.target.value)}
          placeholder={isReply ? "Tell the AI what to reply (optional)" : "Tell the AI what to write, e.g. follow up on Tuesday's meeting"} />
        <button type="button" className="secondary" disabled={!!busy || (!isReply && !instruction.trim())}
          onClick={() => assist(isReply ? "reply" : "draft")}>{busy === "draft" || busy === "reply" ? "Writing…" : "Write with AI"}</button>
      </div>
      {body.trim() && (
        <div className="ai-actions">
          <span className="muted small">AI:</span>
          {([["improve", "Improve"], ["shorter", "Shorter"], ["formal", "More formal"], ["friendly", "Friendlier"], ["english", "To English"]] as [Assist, string][]).map(([a, label]) => (
            <button key={a} type="button" className="chip small-chip" disabled={!!busy} onClick={() => assist(a)}>{busy === a ? "…" : label}</button>
          ))}
          <span className="inline">
            <button type="button" className="chip small-chip" disabled={!!busy} onClick={() => assist("arabic")}>{busy === "arabic" ? "…" : "To Arabic"}</button>
            <select aria-label="Arabic style" value={dialect} onChange={(e) => setDialect(e.target.value)} className="tiny-select">
              {DIALECTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </span>
          {undo && <button type="button" className="secondary small" onClick={() => { setSubject(undo.subject); setBody(undo.body); setUndo(null); }}>Undo</button>}
        </div>
      )}
      <ErrorText error={error} />
      <div className="inline">
        <button type="button" disabled={!!busy || !body.trim() || (!isReply && (!subject.trim() || !from))} onClick={send}>{busy === "send" ? "Sending…" : group ? "Send to everyone chosen" : "Send email"}</button>
        {onCancel && <button type="button" className="link" onClick={onCancel}>Cancel</button>}
        {sent && <span className="muted small">Sent. It appears in the inbox conversation.</span>}
      </div>
      <p className="muted small" style={{ margin: ".4rem 0 0" }}>AI only suggests text; nothing is sent until you press Send. Check names, dates and prices.</p>
    </div>
  );
}
